import { throttle } from 'lodash';
import {
  ANY_RID,
  SbRrwebEvents,
  RrwebEventAcks,
  RrwebEventAcksSerializeable,
  EventType,
  CustomRrwebState,
  CustomRrwebStateEvent,
} from 'sb-lib/types';
import { store } from 'shared/store';
import { updateRrwebIngressHealth } from 'shared/store/dashboard-slice';
import { sendResetRrweb } from 'shared/store/realtime-slice';
import { makeLog } from 'shared/utils/log';
import {
  cloneRrwebMouseEventForWidget,
  isRrwebMouseMoveEvent,
  massageClonedRrwebMouseEventsForWidget,
} from 'shared/utils/sb-rrweb';
import {
  emitRrwebEventFromVisitor,
  emitRrwebWidgetEventFromVisitor,
  sendRrwebEventAcks,
} from './manager';
import { recordRrwebEvents, recordWidgetRrwebEvents } from './rrweb-event-sink';

/** How long to wait to send incomplete queue to rrweb player */
const NONCE_QUEUE_TIMEOUT = 20000;
/** How often to send rrweb event ACKs to widget */
const RRWEB_EVENT_ACKS_THROTTLE = 500;
/** Useed when comparing similar events to determine if they are stale or not */
export const RID_RELATIVE_STALE_TIME = 5000;

const log = makeLog('rrweb-event-handler');

let latestEvent: (SbRrwebEvents & { timestamp: number }) | undefined;
let latestWidgetEvent: (SbRrwebEvents & { timestamp: number }) | undefined;
export let latestMainEvent: (SbRrwebEvents & { timestamp: number }) | undefined;
let cachedCustomState: CustomRrwebState | undefined;

export const setLatestMainEvent = (event: SbRrwebEvents) => {
  if (event.events.length) {
    latestMainEvent = { ...event, timestamp: Date.now() };
  }
};
export const setLatestWidgetEvent = (event: SbRrwebEvents) => {
  if (event.events.length) {
    latestWidgetEvent = { ...event, timestamp: Date.now() };
  }
};

/** ACKs we want to send to widget */
let eventAckQueue: RrwebEventAcks = {};

/** Received rid+nonces to check to avoid re-play resends from widget (due to late ACK) */
export const eventsReceived = new Map<string, number>();

export const handleRrwebEvent = (event: SbRrwebEvents, nonce: number) => {
  // ignore events we've already received, they may have been re-sent due to a late ACK
  if (hasReceivedRrwebEvent(event.rid, nonce)) {
    log.info(`Ignoring event already received ${event.rid}:${nonce}, will resend ACK`);
    // just in case the ACK itself was missed, re-queue an ACK for this event
    // this avoids the widget getting stuck trying to send an event for which it missed the ACK
    queueEventAck(event.rid, nonce);
    return;
  }
  // Handle rrweb events that come in out of order. If this event was queued,
  // skip for now and it will be called later.
  queueEventAck(event.rid, nonce);
  processRrwebEvent(event, nonce);
};

const processRrwebEvent = (event: SbRrwebEvents, nonce: number) => {
  // Handle rrweb events that come in out of order. If this event was queued,
  // skip for now and it will be called later.
  const wasQueued = handleNonceQueue(event, nonce);
  if (wasQueued) {
    return;
  }

  if (event.type === 'rrwebEvent') {
    const diverted = divertRrwebMouseEventDuringIframe(event);
    event = diverted.event;
    if (diverted.mouseEvent) {
      emitRrwebWidgetEventFromVisitor(diverted.mouseEvent);
      recordWidgetRrwebEvents(diverted.mouseEvent.events);
    }
  }

  if (event.type === 'rrwebEvent') {
    setLatestMainEvent(event);
    emitRrwebEventFromVisitor(event);
    recordRrwebEvents(event.events);
  } else if (event.type === 'rrwebWidgetEvent') {
    setLatestWidgetEvent(event);
    emitRrwebWidgetEventFromVisitor(event);
    recordWidgetRrwebEvents(event.events);
  }

  // Track latest event
  if (event.events.length) {
    const previousRid = latestEvent?.rid;
    latestEvent = {
      ...event,
      timestamp: Date.now(),
    };
    if (latestEvent.rid !== previousRid && cachedCustomState) {
      log.debug('Emitting cached custom rrweb state');
      emitCustomRrwebStateEvent(cachedCustomState);
    }
  }
};

export const emitCustomRrwebStateEvent = (state: CustomRrwebState) => {
  if (!latestEvent || !latestEvent.events.length) {
    log.debug(
      'Tried to emit custom rrweb state before any real events, caching until first event',
    );
    cachedCustomState = state;
    return;
  }
  cachedCustomState = undefined;

  // Calculate custom event timestamp based on delta between last event's timestamp
  // (recorded locally) and latest inner event's timestamp (recorded by visitor)
  // and offs
  const delta = Date.now() - latestEvent.timestamp;
  const lastInnerEvent = latestEvent.events[latestEvent.events.length - 1];
  const timestamp = lastInnerEvent.timestamp + delta;
  const event: CustomRrwebStateEvent = {
    type: EventType.Custom,
    data: {
      tag: 'sb-custom-state',
      payload: state,
    },
  };
  const customEvent: SbRrwebEvents = {
    type: 'rrwebEvent',
    events: [{ ...event, timestamp, rid: ANY_RID, mode: latestEvent.mode }],
    rid: ANY_RID,
    mode: latestEvent.mode,
  };

  log.debug('Emitting custom state rrweb event', event);
  emitRrwebEventFromVisitor(customEvent);
  recordRrwebEvents(customEvent.events);
};

// Tracks state for handleNonceQueue (export for testing)
export interface NonceQueueState {
  latestNonce: number;
  queuedEvents: { event: SbRrwebEvents; nonce: number }[];
  failsafeTimeout: ReturnType<typeof setTimeout> | undefined;
  latestEventTime: number;
  type: SbRrwebEvents['type'];
  queueStartTime: number;
  lastDrainLatency: number;
}

export const makeNonceQueueState = (type: SbRrwebEvents['type']) =>
  ({
    latestNonce: 0,
    queuedEvents: [],
    failsafeTimeout: undefined,
    latestEventTime: 0,
    type,
    queueStartTime: 0,
    lastDrainLatency: 0,
  } as NonceQueueState);

export let ridStateMap: Record<string, NonceQueueState | undefined> = {};

function clearNonceQueue() {
  const oldRidStateMap = ridStateMap;
  ridStateMap = {};
  const results: string[] = [];
  Object.entries(oldRidStateMap).forEach(([k, v]) => {
    let result = `rid=${k} events=${v?.queuedEvents.length} type=${v?.type}`;
    if (v?.failsafeTimeout) {
      clearTimeout(v.failsafeTimeout);
      result += ' [to cleared]';
    }
    results.push(result);
  });
  log.debug(`Cleared nonce queue ${results}`);
}

/**
 * Given an event and its nonce, determine if it's the next event expected in
 * order, or if it's come out of order. Events that come in out of order will
 * be added to a queue. All events in the queue will be played back in order
 * either A) when the correct nonce event comes through, or B) after a healthy
 * timeout.
 * @returns bool - whether or not the event needed to be queued for later emission.
 */
const handleNonceQueue = (event: SbRrwebEvents, nonce: number) => {
  // Events without rids can't be handled. God save you.
  if (!event.rid) {
    return false;
  }

  // If we haven't encountered this rid, initialize the state map.
  const state = ridStateMap[event.rid] || makeNonceQueueState(event.type);
  if (!ridStateMap[event.rid]) {
    ridStateMap[event.rid] = state;
  }

  // Always update the latest event time
  state.latestEventTime = Date.now();

  // If it's the right nonce with no queue, skip the pageantry.
  const isCorrectNonce = nonce === state.latestNonce + 1;
  if (isCorrectNonce && !state.queuedEvents.length) {
    state.latestNonce = nonce;
    return false;
  }

  // Warn if this nonce is below the latest one, but play immediately since it's very late.
  // Don't update latestNonce to a lower number though.
  if (nonce < state.latestNonce) {
    log.warn(
      `Received rrweb event ${event.rid}:${nonce} long after expected, currently on ${state.latestNonce}, playing it back anyway`,
    );
    return false;
  }

  // If the queue is empty, set the start time since it will be getting an entry
  if (!state.queuedEvents.length) {
    state.queueStartTime = Date.now();
  }

  // Append to queue, clear it out now (if correct nonce) or after a timeout (if not already set)
  state.queuedEvents.push({ nonce, event });

  const playQueueInOrder = () => {
    state.failsafeTimeout = undefined;
    const sortedEvents = [...state.queuedEvents].sort(
      ({ nonce: n1 }, { nonce: n2 }) => n1 - n2,
    );
    state.latestNonce = sortedEvents[0].nonce - 1;
    state.queuedEvents = [];

    log.debug(
      `Playing queue of ${sortedEvents.length} rrweb events, rid:lastNonce [${event.rid}:${nonce}] `,
    );

    state.lastDrainLatency = Date.now() - state.queueStartTime;
    state.queueStartTime = 0;
    storeQueueHealth(state);
    sortedEvents.forEach(({ event, nonce }) => processRrwebEvent(event, nonce));
  };

  if (isCorrectNonce) {
    state.failsafeTimeout && clearTimeout(state.failsafeTimeout);
    playQueueInOrder();
  } else if (!state.failsafeTimeout) {
    state.failsafeTimeout = setTimeout(() => {
      // Check if there is a newer rid queue
      const staleReason = isNonceQueueStale(event.rid, state);
      if (staleReason) {
        log.info(
          `Dropping nonce queue because ${explainStaleReason(
            event.rid,
            state,
            staleReason,
          )}`,
        );
        delete ridStateMap[event.rid];
      } else {
        log.warn(
          `Exceeded ${NONCE_QUEUE_TIMEOUT}ms rrweb nonce queue timeout for ${event.rid}, will send reset request and try to playback events anyway`,
        );
        store.dispatch(sendResetRrweb());
        playQueueInOrder();
      }
    }, NONCE_QUEUE_TIMEOUT);
  }
  return true;
};

const rootRidIsNewer = (rid: string, ridOther: string) => {
  if (!ridOther) return false;
  const [root, nonce] = rid.split('.');
  const [otherRoot, otherNonce] = ridOther.split('.');
  return root === otherRoot && parseInt(otherNonce) > parseInt(nonce);
};

const eventTimestampIsNewer = (
  a: NonceQueueState,
  b: NonceQueueState | typeof latestEvent,
) => {
  if (!b) return false;
  const bTime = 'latestEventTime' in b ? b.latestEventTime : b.timestamp;
  return a.latestEventTime - bTime < -RID_RELATIVE_STALE_TIME;
};

export type StaleReason =
  | 'NOT_IN_QUEUE'
  | 'NEWER_ROOT_RID'
  | 'NEWER_TIMESTAMP'
  | 'NEWER_QUEUE_ROOT_RID'
  | 'NEWER_QUEUE_TIMESTAMP';
export type StaleReturn = {
  reason: StaleReason;
  newerRid: string | null;
  newerTimestamp: number | null;
};
const explainStaleReason = (rid: string, state: NonceQueueState, reason: StaleReturn) => {
  const timeDiff = (reason.newerTimestamp || 0) - state.latestEventTime;
  return `(rid=${rid}, type=${state.type}) is stale (reason=${reason.reason}, rid=${reason.newerRid}, newerByMs=${timeDiff})`;
};
export const isNonceQueueStale = (
  nqRid: string,
  nqState: NonceQueueState,
): StaleReturn | false => {
  // If the rid isn't in the global map, then its stale
  if (!ridStateMap[nqRid]) {
    return {
      reason: 'NOT_IN_QUEUE',
      newerRid: null,
      newerTimestamp: null,
    };
  }

  const latestEventOfSameType =
    nqState.type === 'rrwebEvent' ? latestMainEvent : latestWidgetEvent;

  // Check againts the latest event of same type for ridNonce or timestamp newer
  if (latestEventOfSameType && latestEventOfSameType.rid !== nqRid) {
    if (rootRidIsNewer(nqRid, latestEventOfSameType.rid)) {
      return {
        reason: 'NEWER_ROOT_RID',
        newerRid: latestEventOfSameType.rid,
        newerTimestamp: latestEventOfSameType.timestamp,
      };
    }
    if (eventTimestampIsNewer(nqState, latestEventOfSameType)) {
      return {
        reason: 'NEWER_TIMESTAMP',
        newerRid: latestEventOfSameType.rid,
        newerTimestamp: latestEventOfSameType.timestamp,
      };
    }
  }

  // Check against existing queues of same type for newer ridNonce or timestamp
  const eventStatesOfSameType = Object.entries(ridStateMap).filter(
    ([rid, state]) => rid != nqRid && state && state.type === nqState.type,
  );

  // ... newer ridNonce
  const newerRidNonces = eventStatesOfSameType.filter(([rid, _]) =>
    rootRidIsNewer(nqRid, rid),
  );
  if (newerRidNonces.length) {
    const newerTimestamp = newerRidNonces[0][1]?.latestEventTime || 0;
    return {
      reason: 'NEWER_QUEUE_ROOT_RID',
      newerRid: newerRidNonces[0][0],
      newerTimestamp,
    };
  }

  // ... newer timestamp
  const newerTimes = eventStatesOfSameType.filter(
    ([_, state]) => state && eventTimestampIsNewer(nqState, state),
  );
  if (newerTimes.length) {
    const newerTimestamp = newerTimes[0][1]?.latestEventTime || 0;
    return {
      reason: 'NEWER_QUEUE_TIMESTAMP',
      newerRid: newerTimes[0][0],
      newerTimestamp,
    };
  }

  return false;
};

const storeQueueHealthFollowupTimer = 0;
let lastDuplicateEventTime = 0;
const currentQueueHealth = {
  timeWindow: 5000,
  time: 0,
  latency: 0,
};
const storeQueueHealth = (queueState: NonceQueueState) => {
  clearTimeout(storeQueueHealthFollowupTimer);
  const { time, latency, timeWindow } = currentQueueHealth;
  const isStoredLatencyExpired = Date.now() - time > timeWindow;
  const isNewLatencyLarger = latency < queueState.lastDrainLatency;
  if (isStoredLatencyExpired || isNewLatencyLarger) {
    currentQueueHealth.time = Date.now();
    currentQueueHealth.latency = queueState.lastDrainLatency;
    reportRrwebIngressHealth();
  } else {
    setTimeout(() => storeQueueHealth(queueState), timeWindow);
  }
};

const reportRrwebIngressHealth = throttle(() => {
  store.dispatch(
    updateRrwebIngressHealth({
      queueLatency: currentQueueHealth.latency,
      lastDuplicateTime: lastDuplicateEventTime,
    }),
  );
}, 1000);

const queueEventAck = (rid: string, nonce: number) => {
  eventAckQueue[rid] = eventAckQueue[rid] || new Set();
  eventAckQueue[rid].add(nonce);
  sendEventAcks();
};

const clearAckQueue = () => {
  const oldQueue = eventAckQueue;
  eventAckQueue = {}; // clear it
  const rids = Object.entries(oldQueue).map(([k, v]) => `rid=${k}(acks=${v.size})`);
  log.debug(`Cleared ack queue of rids: ${rids}`);
};

/** Drain the ACKs queue and send them to widget (throttled) */
const sendEventAcks = throttle(() => {
  // convert Set to array
  const serializeableAckQueue: RrwebEventAcksSerializeable = Object.entries(
    eventAckQueue,
  ).reduce((acc, [rid, nonces]) => ({ ...acc, [rid]: [...nonces] }), {});
  const wasSent = sendRrwebEventAcks(serializeableAckQueue);
  if (wasSent) {
    eventAckQueue = {};
  }
}, RRWEB_EVENT_ACKS_THROTTLE);

/** Check if the rid+nonce has been received and clean up registry
 *  of received events if it becomes too large.
 * @returns bool - Has the event been received?
 * */
const hasReceivedRrwebEvent = (rid: string, nonce: number) => {
  const eventUid = `${rid}:${nonce}`;
  const isReceived = eventsReceived.has(eventUid);
  !isReceived && eventsReceived.set(eventUid, Date.now());
  if (isReceived) {
    lastDuplicateEventTime = Date.now();
    reportRrwebIngressHealth();
  }
  // make sure we don't gobble too much memory by removing stales
  // these are just the rid+nonce so they are not big
  if (eventsReceived.size > 10000) {
    // defer so as not to delay handler
    setTimeout(() => {
      const toRem: string[] = [];
      const now = Date.now();
      Object.entries(eventsReceived).forEach(([eventUid, receivedTime]) => {
        if (now - receivedTime > 10000) {
          toRem.push(eventUid);
        }
      });
      if (toRem.length) {
        log.debug(`Clearing out ${toRem.length} old received event ids`);
        toRem.forEach(eventId => eventsReceived.delete(eventId));
      }
    }, 1);
  }
  return isReceived;
};

const divertRrwebMouseEventDuringIframe = (event: SbRrwebEvents) => {
  let mouseEvent: SbRrwebEvents | undefined = undefined;
  // divert mouse moves to widget rrweb when have rec'd widget events (widget iframe mode)
  if (event.type === 'rrwebEvent' && event.mode === 'iframe') {
    const mouseRawEvents = event.events.filter(isRrwebMouseMoveEvent);
    const nonMouseRawEvents = event.events.filter(e => !isRrwebMouseMoveEvent(e));

    if (mouseRawEvents.length) {
      mouseEvent = cloneRrwebMouseEventForWidget(event);
      mouseEvent.events = mouseRawEvents;
      massageClonedRrwebMouseEventsForWidget(mouseEvent);

      // keep the non-mouse events on the original event
      event.events = nonMouseRawEvents;
    }
  }

  return { event, mouseEvent };
};

export function rrwebEventHandlerClearAll() {
  log.debug(`Clear all rrweb event queues`);
  clearAckQueue();
  clearNonceQueue();
  latestEvent = undefined;
  latestWidgetEvent = undefined;
  latestMainEvent = undefined;
  cachedCustomState = undefined;
}
